import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearAdminSession, getAdminSession, subscribeToAdminAuth } from "../../utils/adminSession";

const adminNavItems = [
  { path: "/admin/dashboard", label: "Knowledge Center" },
  { path: "/admin/testimonials", label: "Testimonials" },
  { path: "/admin/bookings", label: "Bookings" },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState(() => getAdminSession());
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  useEffect(() => {
    const syncAuthState = (session = getAdminSession()) => {
      setAuthState(session);

      if (!session.isLoggedIn) {
        navigate("/admin/login", { replace: true });
      }
    };

    syncAuthState();
    const unsubscribe = subscribeToAdminAuth(syncAuthState);

    return unsubscribe;
  }, [navigate]);

  const adminUser = useMemo(() => authState.admin, [authState.admin]);
  const profileName = adminUser?.userName || "Admin User";
  const profileInitial = profileName.trim().toLowerCase() === "user admin" ? "A" : (profileName.trim().charAt(0) || "A").toUpperCase();
  const isKnowledgeCenterActive = location.pathname === "/admin/dashboard";
  const isTestimonialsActive = location.pathname === "/admin/testimonials";
  const isBookingsActive = location.pathname === "/admin/bookings";

  const handleLogout = () => {
    clearAdminSession();
    setIsProfileOpen(false);
    navigate("/", { replace: true });
  };

  const handleProfileToggle = () => {
    setIsProfileOpen((prev) => !prev);
  };

  if (!authState.isLoggedIn) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-screen-2xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-2xl font-semibold tracking-tight text-slate-900">AegisCare Admin</p>

            <div className="relative flex items-center">
              <button
                type="button"
                onClick={handleProfileToggle}
                className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                  {profileInitial}
                </span>
              </button>

              {isProfileOpen ? (
                <div className="absolute right-0 top-full z-10 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
                  <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Name</p>
                      <p className="mt-1 text-sm font-medium text-slate-900">{profileName}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Email</p>
                      <p className="mt-1 text-sm font-medium text-slate-900">
                        {adminUser?.email || "admin@gmail.com"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="mt-1 w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <nav className="mt-4 flex items-center gap-3 text-sm font-medium text-slate-600">
            {adminNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={() =>
                  `rounded-md px-1 py-0.5 transition ${
                    item.path === "/admin/dashboard"
                      ? isKnowledgeCenterActive
                        ? "text-slate-900 underline decoration-slate-900 decoration-2 underline-offset-8"
                        : "hover:text-slate-900"
                      : item.path === "/admin/testimonials"
                        ? isTestimonialsActive
                          ? "text-slate-900 underline decoration-slate-900 decoration-2 underline-offset-8"
                          : "hover:text-slate-900"
                        : isBookingsActive
                          ? "text-slate-900 underline decoration-slate-900 decoration-2 underline-offset-8"
                          : "hover:text-slate-900"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <div className="mx-auto max-w-screen-2xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
